import {
  useState,
  useEffect,
  useRef,
  useCallback,
  createContext,
  useContext,
} from "react";
import axios from "axios";
import { API } from "@/App";
import { isNativeApp, sendToNative } from "@/lib/native";

const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}` });

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const value = useNotificationState();
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error(
      "useNotifications must be used within NotificationProvider",
    );
  }
  return ctx;
}

function useNotificationState() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sseStatus, setSseStatus] = useState("disconnected"); // disconnected | connecting | connected | error

  // Refs for SSE lifecycle
  const abortRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const mountedRef = useRef(true);

  // Fetch paginated notifications
  const fetchNotifications = useCallback(async (pageNum = 1, reset = false) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/v1/notifications`, {
        params: { page: pageNum, page_size: 20 },
        headers: headers(),
      });
      const data = res.data;
      const items = data.items || data.notifications || [];
      setNotifications((prev) => (reset ? items : [...prev, ...items]));
      setHasMore(items.length === 20);
      setPage(pageNum);
      if (data.unread_count !== undefined) setUnreadCount(data.unread_count);
    } catch (e) {
      console.error("Failed to fetch notifications", e);
    }
    setLoading(false);
  }, []);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/v1/notifications/unread-count`, {
        headers: headers(),
      });
      setUnreadCount(res.data.unread_count ?? 0);
    } catch (e) {
      console.error("Failed to fetch unread count", e);
    }
  }, []);

  // Mark single notification as read
  const markAsRead = useCallback(async (id) => {
    try {
      await axios.post(
        `${API}/v1/notifications/${id}/read`,
        {},
        { headers: headers() },
      );
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) {
      console.error("Failed to mark notification as read", e);
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await axios.post(
        `${API}/v1/notifications/read-all`,
        {},
        { headers: headers() },
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error("Failed to mark all as read", e);
    }
  }, []);

  // Load next page
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchNotifications(page + 1);
    }
  }, [loading, hasMore, page, fetchNotifications]);

  // Parse SSE frames from buffer. SSE events are separated by "\n\n".
  const parseSSEBuffer = useCallback((buffer) => {
    const events = [];
    const parts = buffer.split("\n\n");
    // Last part may be incomplete - keep as remaining buffer
    const remaining = parts.pop() || "";

    for (const part of parts) {
      const lines = part.split("\n");
      let data = "";
      for (const line of lines) {
        // Skip heartbeat comments
        if (line.startsWith(":")) continue;
        // Accumulate data fields (multi-line support)
        if (line.startsWith("data: ")) {
          data += (data ? "\n" : "") + line.slice(6);
        } else if (line.startsWith("data:")) {
          data += (data ? "\n" : "") + line.slice(5);
        }
      }
      if (data) {
        try {
          events.push(JSON.parse(data));
        } catch (_) {
          // Skip malformed JSON
        }
      }
    }
    return { events, remaining };
  }, []);

  // Ref to hold connectSSE for use inside scheduleReconnect (breaks circular dep)
  const connectRef = useRef(null);

  // Schedule reconnect with exponential backoff (3s, 6s, 12s, max 30s)
  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    const delay = Math.min(3000 * Math.pow(2, retryCountRef.current), 30000);
    retryCountRef.current += 1;
    setSseStatus("error");
    retryTimerRef.current = setTimeout(() => {
      if (mountedRef.current && connectRef.current) connectRef.current();
    }, delay);
  }, []);

  // SSE connection via fetch + ReadableStream
  const connectSSE = useCallback(() => {
    // Prevent duplicate connections
    if (abortRef.current) return;
    const t = token();
    if (!t) return;

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setSseStatus("connecting");

    const url = `${API}/v1/notifications/stream`;

    (async () => {
      try {
        const resp = await fetch(url, {
          headers: { Authorization: `Bearer ${t}` },
          signal: ctrl.signal,
        });

        if (!resp.ok) {
          console.error("SSE connect failed:", resp.status);
          abortRef.current = null;
          // Don't retry on auth errors
          if (resp.status === 401 || resp.status === 403) {
            setSseStatus("error");
            return;
          }
          scheduleReconnect();
          return;
        }

        // Connected successfully - reset retry counter
        retryCountRef.current = 0;
        setSseStatus("connected");

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const { events, remaining } = parseSSEBuffer(buffer);
          buffer = remaining;

          for (const payload of events) {
            setNotifications((prev) => [payload, ...prev]);
            setUnreadCount((c) => c + 1);

            // Trigger native push notification in WebView app
            if (isNativeApp()) {
              sendToNative("push_notification", {
                title: payload.actor?.name || "Zinformed",
                body: payload.message || "You have a new notification",
                data: {
                  id: payload.id,
                  notification_type: payload.notification_type,
                  discussion_id: payload.discussion_id,
                  news_id: payload.news_id,
                  actor_id: payload.actor_id,
                },
              });
            }
          }
        }

        // Stream ended normally - try to reconnect
        abortRef.current = null;
        scheduleReconnect();
      } catch (e) {
        abortRef.current = null;
        if (e.name === "AbortError") {
          // Manual disconnect - don't reconnect
          setSseStatus("disconnected");
          return;
        }
        console.error("SSE error:", e);
        scheduleReconnect();
      }
    })();
  }, [parseSSEBuffer, scheduleReconnect]);

  // Keep ref in sync for reconnect scheduling
  useEffect(() => {
    connectRef.current = connectSSE;
  }, [connectSSE]);

  const disconnectSSE = useCallback(() => {
    // Clear pending retry
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    // Abort active connection
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    retryCountRef.current = 0;
    setSseStatus("disconnected");
  }, []);

  // Mount/unmount lifecycle
  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    (async () => {
      if (!cancelled) await fetchUnreadCount();
    })();
    connectSSE();
    return () => {
      cancelled = true;
      mountedRef.current = false;
      disconnectSSE();
    };
  }, [fetchUnreadCount, connectSSE, disconnectSSE]);

  return {
    notifications,
    unreadCount,
    loading,
    hasMore,
    sseStatus,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    loadMore,
  };
}

export function timeAgo(dateStr) {
  if (!dateStr) return "";
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}
